import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { formatDate, formatDateTime, parseAppDate } from "../../utils/dateTime";
import { useNavigate, useParams } from "react-router-dom";
import moment from "moment";
import { useDispatch, useSelector } from "react-redux";
import { FaArrowLeft, FaSave } from "react-icons/fa";
import { MdDelete, MdOpenInNew } from "react-icons/md";
import { useReactToPrint } from "react-to-print";
import { CustomerCompleteLedgerColumns } from "../../assets/Columns/CustomerCompleteLedgerColumns";
import { fetchCustomers } from "../../store/Slices/CustomerSlice";
import { fetchCustomerLedger } from "../../store/Slices/CustomerLedgerSlice";
import ReportDateRangeBar, { localYmd } from "./ReportDateRangeBar";
import {
  CreateCustomerLedgerRecordApi,
  DeleteCustomerLedgerRecordApi,
  GetCustomerLedgerRecordsApi,
} from "../../ApiRequests";
import { ErrorToast, SuccessToast, WarningToast } from "../../utils/ShowToast";

const TAB_KEYS = ["All", "SH", "Item", "Cash", "Return"];

/** Inclusive local-day unix bounds for YYYY-MM-DD (empty = no bound). */
const filterRowsByDateRange = (rows = [], fromYmd = "", toYmd = "") => {
  if (!fromYmd && !toYmd) return rows || [];
  let start = -Infinity;
  let end = Infinity;
  if (fromYmd) {
    const [y, m, d] = fromYmd.split("-").map(Number);
    start = Math.floor(new Date(y, m - 1, d, 0, 0, 0).getTime() / 1000);
  }
  if (toYmd) {
    const [y, m, d] = toYmd.split("-").map(Number);
    end = Math.floor(new Date(y, m - 1, d, 23, 59, 59).getTime() / 1000);
  }
  return (rows || []).filter((r) => {
    if (r?._isOpening || String(r?.name) === "Opening Balance") return false;
    const t = Number(r?.date) || 0;
    return t >= start && t <= end;
  });
};

const isOpeningRow = (row) =>
  Boolean(row?._isOpening) || String(row?.name) === "Opening Balance";

/**
 * Opening for the visible range: customer OB + net of rows before From date.
 */
const resolveOpeningAmount = (
  customerOpening = 0,
  allTabRows = [],
  fromYmd = "",
) => {
  let bal = Number(customerOpening) || 0;
  if (!fromYmd) return bal;
  const [y, m, d] = fromYmd.split("-").map(Number);
  const start = Math.floor(new Date(y, m - 1, d, 0, 0, 0).getTime() / 1000);
  for (const row of allTabRows || []) {
    if (isOpeningRow(row)) continue;
    if ((Number(row?.date) || 0) < start) {
      bal += Number(row.cr || 0) - Number(row.dr || 0);
    }
  }
  return bal;
};

/** Prepend Opening Balance, then running CR − DR from that start. */
const withOpeningBalance = (rows = [], openingAmt = 0, sortDir = "desc") => {
  const amt = Number(openingAmt) || 0;
  const openingRow = {
    invoice: "-",
    name: "Opening Balance",
    desc: "Opening Balance",
    date: 0,
    n_date: "-",
    qty: 0,
    price: 0,
    discount: 0,
    unit: "",
    cr: amt >= 0 ? amt : 0,
    dr: amt < 0 ? Math.abs(amt) : 0,
    _isOpening: true,
    _rowKey: "opening-balance",
  };
  const rest = (rows || []).filter((r) => !isOpeningRow(r));
  const withBal = recomputeBalances([openingRow, ...cloneRows(rest)], sortDir);
  // Always show Opening at the top; balances already include it chronologically
  const opening = withBal.find((r) => isOpeningRow(r));
  const others = withBal.filter((r) => !isOpeningRow(r));
  return opening ? [opening, ...others] : others;
};

const cloneRows = (rows = []) =>
  (rows || []).map((row, index) => ({
    ...row,
    _rowKey: `${row.invoice || "r"}-${row.date || 0}-${index}`,
  }));

/** Match lines across saved vs live without using price/amount (those can be edited). */
const rowIdentity = (r) =>
  [
    String(r?.invoice ?? ""),
    Number(r?.date) || 0,
    String(r?.name ?? ""),
    Number(r?.qty) || 0,
    String(r?.desc ?? ""),
  ].join("|");

const pickTabRows = (data, tab) => {
  if (!data || Array.isArray(data)) return [];
  if (tab === "SH") return Array.isArray(data.ShAll) ? data.ShAll : [];
  if (tab === "All") return Array.isArray(data.All) ? data.All : [];
  if (tab === "Item") return Array.isArray(data.Item) ? data.Item : [];
  if (tab === "Cash") return Array.isArray(data.Cash) ? data.Cash : [];
  if (tab === "Return") return Array.isArray(data.Return) ? data.Return : [];
  return [];
};

/** Balance oldest→newest, then return in asc or desc display order. */
const recomputeBalances = (rows = [], sortDir = "desc") => {
  const indexed = (rows || []).map((row, index) => ({
    ...row,
    __stable: index,
  }));
  const asc = [...indexed].sort((a, b) => {
    const dateDiff = Number(a.date || 0) - Number(b.date || 0);
    if (dateDiff !== 0) return dateDiff;
    const inv = String(a.invoice || "").localeCompare(String(b.invoice || ""));
    if (inv !== 0) return inv;
    const name = String(a.name || "").localeCompare(String(b.name || ""));
    if (name !== 0) return name;
    const key = String(a._rowKey || "").localeCompare(String(b._rowKey || ""));
    if (key !== 0) return key;
    return a.__stable - b.__stable;
  });
  let balance = 0;
  const withBal = asc.map(({ __stable, ...row }) => {
    balance += Number(row.cr || 0);
    balance -= Number(row.dr || 0);
    return { ...row, balance };
  });
  if (sortDir === "asc") return withBal;
  return [...withBal].reverse();
};

/** Recalc balances without changing current row order (avoids jump while editing). */
const refreshBalancesPreserveOrder = (rows = []) => {
  const list = rows || [];
  const indexed = list.map((row, index) => ({ row, index }));
  indexed.sort((a, b) => {
    const dateDiff = Number(a.row.date || 0) - Number(b.row.date || 0);
    if (dateDiff !== 0) return dateDiff;
    const inv = String(a.row.invoice || "").localeCompare(
      String(b.row.invoice || ""),
    );
    if (inv !== 0) return inv;
    const name = String(a.row.name || "").localeCompare(
      String(b.row.name || ""),
    );
    if (name !== 0) return name;
    const key = String(a.row._rowKey || "").localeCompare(
      String(b.row._rowKey || ""),
    );
    if (key !== 0) return key;
    return a.index - b.index;
  });
  let balance = 0;
  const balByKey = new Map();
  const balByIndex = new Map();
  for (const { row, index } of indexed) {
    balance += Number(row.cr || 0);
    balance -= Number(row.dr || 0);
    if (row._rowKey != null) balByKey.set(row._rowKey, balance);
    balByIndex.set(index, balance);
  }
  return list.map((row, index) => ({
    ...row,
    balance:
      row._rowKey != null && balByKey.has(row._rowKey)
        ? balByKey.get(row._rowKey)
        : (balByIndex.get(index) ?? row.balance),
  }));
};

/**
 * Keep saved/edited prices on matching lines; append any new live lines
 * (added after the snapshot was saved).
 */
const mergeEditsWithLive = (editedRows, liveRows, sortDir = "desc") => {
  const editsByKey = new Map();
  (editedRows || []).forEach((r) => {
    editsByKey.set(rowIdentity(r), r);
  });
  const liveKeys = new Set();
  const merged = [];

  for (const live of liveRows || []) {
    const key = rowIdentity(live);
    liveKeys.add(key);
    const edited = editsByKey.get(key);
    if (edited) {
      merged.push({
        ...live,
        price: edited.price,
        cr: edited.cr,
        dr: edited.dr,
      });
    } else {
      merged.push({ ...live });
    }
  }

  for (const [key, edited] of editsByKey.entries()) {
    if (!liveKeys.has(key)) merged.push({ ...edited });
  }

  return recomputeBalances(cloneRows(merged), sortDir);
};

const applyPriceToRow = (row, newPrice) => {
  const price = Number(newPrice);
  const qty = Number(row.qty || 0);
  const next = {
    ...row,
    price: Number.isFinite(price) ? price : row.price,
  };
  if (!Number.isFinite(price) || qty <= 0) return next;
  const amount = price * qty;
  // Return / item debit lines with a product name
  if (Number(row.dr || 0) > 0 && row.name && row.name !== "-") {
    next.dr = amount;
    return next;
  }
  // Sale / credit lines
  next.cr = amount;
  return next;
};

const visibleColumns = (tab) =>
  CustomerCompleteLedgerColumns.filter((dt) => {
    if (dt.id === "discount") return false;
    // Name cell: Name (top) + Desc (below)
    if (dt.id === "desc") return false;
    // Date cell: Date (top) + Invoice no (bold, below)
    if (dt.id === "invoice") return false;
    if (tab === "Item") return dt.id !== "dr";
    if (tab === "Cash") {
      return dt.id !== "cr" && dt.id !== "qty" && dt.id !== "price";
    }
    if (tab === "Return") return dt.id !== "cr";
    return true;
  });

/** Row tint so Invoice / SH / Return / Payment / Discount are easy to spot. */
const ledgerRowKind = (row) => {
  const dr = Number(row?.dr || 0);
  const cr = Number(row?.cr || 0);
  const qty = Number(row?.qty || 0);
  const disc = Number(row?.discount || 0);
  const name = String(row?.name || "");
  const isShopping =
    String(row?.unit || "").toUpperCase() === "SH" ||
    name === "Shopping" ||
    String(row?.type || "").toUpperCase() === "SH";

  if (isOpeningRow(row)) {
    return "opening";
  }
  if (name === "Direct Discount" || (disc > 0 && qty === 0 && dr > 0)) {
    return "discount";
  }
  if (dr > 0 && qty > 0 && name && name !== "-") {
    return "return";
  }
  if (dr > 0) {
    return "payment";
  }
  if (cr > 0 && isShopping) {
    return "shopping";
  }
  if (cr > 0) {
    return "box";
  }
  return "other";
};

const LEDGER_ROW_COLORS = {
  opening: "bg-slate-100 text-slate-900",
  box: "bg-sky-100 text-sky-950",
  shopping: "bg-fuchsia-100 text-fuchsia-950",
  return: "bg-amber-50 text-amber-950",
  payment: "bg-emerald-50 text-emerald-950",
  discount: "bg-violet-50 text-violet-950",
  other: "text-main",
};

const LEDGER_ROW_LEGEND = [
  {
    key: "opening",
    label: "Opening",
    className: "bg-slate-100 border-slate-300",
  },
  {
    key: "box",
    label: "Not SH",
    className: "bg-sky-100 border-sky-300",
  },
  {
    key: "shopping",
    label: "SH",
    className: "bg-fuchsia-100 border-fuchsia-300",
  },
  { key: "return", label: "Return", className: "bg-amber-50 border-amber-200" },
  {
    key: "payment",
    label: "Payment",
    className: "bg-emerald-50 border-emerald-200",
  },
  {
    key: "discount",
    label: "Discount",
    className: "bg-violet-50 border-violet-200",
  },
];

const formatRs = (n) => `Rs ${Number(n || 0).toLocaleString("en-PK")}`;

const LEDGER_NUMERIC_COLS = new Set([
  "qty",
  "price",
  "discount",
  "cr",
  "dr",
  "balance",
  "payable",
  "paid",
  "total",
]);

const ledgerColAlign = (id) =>
  LEDGER_NUMERIC_COLS.has(id) ? "text-right tabular-nums" : "text-left";

const ledgerColWidth = (id) => {
  switch (id) {
    case "date":
      return "w-[6.5rem] min-w-[6.5rem] max-w-[6.5rem]";
    case "invoice":
      return "w-[6.5rem] whitespace-nowrap";
    case "name":
      return "min-w-[7rem] max-w-[14rem]";
    case "desc":
      return "min-w-[7rem] max-w-[14rem]";
    case "qty":
      return "w-[3.5rem]";
    case "price":
      return "w-[5.5rem]";
    case "discount":
    case "cr":
    case "dr":
    case "balance":
      return "w-[6rem]";
    default:
      return "w-[6.5rem]";
  }
};

/**
 * Totals from the visible ledger rows (date filter + edited prices).
 * Return lines: DR with qty + product name (same as applyPriceToRow).
 * Paid: other DR (payments / NPI debit). Total: sum CR.
 * Discount: sum of invoice discounts on rows (already once per invoice).
 * Total Qty (not SH): sold qty on non-SH / non-Shopping CR lines.
 * Total Shopping (SH): CR amount on SH / Shopping lines.
 */
const figuresFromRows = (rows = []) => {
  let total = 0;
  let paid = 0;
  let returnAmt = 0;
  let discount = 0;
  let opening_balance = 0;
  let total_qty_not_sh = 0;
  let total_shopping_sh = 0;
  for (const row of rows || []) {
    if (isOpeningRow(row)) {
      opening_balance = Number(row.cr || 0) - Number(row.dr || 0);
      continue;
    }
    const cr = Number(row.cr || 0);
    total += cr;
    discount += Number(row.discount || 0);
    if (cr > 0) {
      const isShopping =
        String(row.unit || "").toUpperCase() === "SH" ||
        String(row.name || "") === "Shopping";
      if (isShopping) {
        total_shopping_sh += cr;
      } else {
        total_qty_not_sh += Number(row.qty || 0);
      }
    }
    const dr = Number(row.dr || 0);
    if (dr <= 0) continue;
    // Direct discount: shown in Discount col + DR for balance — not Paid
    if (Number(row.discount || 0) > 0 && Number(row.qty || 0) === 0) {
      continue;
    }
    if (Number(row.qty || 0) > 0 && row.name && row.name !== "-") {
      returnAmt += dr;
    } else {
      paid += dr;
    }
  }
  return {
    total,
    opening_balance,
    discount,
    return_amount: returnAmt,
    recieved: paid,
    recieveable: opening_balance + total - paid - returnAmt,
    total_qty_not_sh,
    total_shopping_sh,
  };
};

const formatRecordWhen = (record) => {
  if (record?.date) return formatDateTime(record.date);
  if (record?.createdAt) return formatDateTime(record.createdAt);
  return "—";
};

const CustomersLegderReport = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const CustomerLedgers = useSelector((state) => state.CustomerLedgers);
  const Customers = useSelector((state) => state.Customers);

  const [CurrentTab, setCurrentTab] = useState("All");
  const [ledgerSort, setLedgerSort] = useState("desc");
  /** Empty = show full ledger (no date filter). */
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [editRows, setEditRows] = useState([]);
  const [dirty, setDirty] = useState(false);
  const [saveLoading, setSaveLoading] = useState(false);
  const [savedRecords, setSavedRecords] = useState([]);
  const [recordsLoading, setRecordsLoading] = useState(false);
  /** When set, main table shows this saved snapshot (editable from here). */
  const [activeSavedId, setActiveSavedId] = useState(null);
  const [activeSavedMeta, setActiveSavedMeta] = useState(null);

  const contentToPrint = useRef(null);
  const hydrateKeyRef = useRef("");
  const skipLiveHydrateRef = useRef(false);

  const customerRecord = useMemo(
    () => (Customers.data || []).find((dt) => String(dt._id) === String(id)),
    [Customers.data, id],
  );

  const sessionBranchId = useMemo(() => {
    try {
      const user = JSON.parse(localStorage.getItem("user") || "{}");
      return user?.branchId || "";
    } catch {
      return "";
    }
  }, []);

  const sourceRows = useMemo(() => {
    const d = CustomerLedgers?.data;
    if (!d || Array.isArray(d)) return [];
    if (CurrentTab === "SH") return Array.isArray(d.ShAll) ? d.ShAll : [];
    if (CurrentTab === "All") return Array.isArray(d.All) ? d.All : [];
    if (CurrentTab === "Item") return Array.isArray(d.Item) ? d.Item : [];
    if (CurrentTab === "Cash") return Array.isArray(d.Cash) ? d.Cash : [];
    if (CurrentTab === "Return") return Array.isArray(d.Return) ? d.Return : [];
    return [];
  }, [CustomerLedgers?.data, CurrentTab]);

  const filteredSourceRows = useMemo(
    () => filterRowsByDateRange(sourceRows, fromDate, toDate),
    [sourceRows, fromDate, toDate],
  );

  const customerOpeningBase = useMemo(() => {
    if (activeSavedId && activeSavedMeta?.customerSnapshot) {
      return Number(activeSavedMeta.customerSnapshot.opening_balance) || 0;
    }
    return Number(customerRecord?.opening_balance) || 0;
  }, [
    activeSavedId,
    activeSavedMeta?.customerSnapshot,
    customerRecord?.opening_balance,
  ]);

  const openingAmount = useMemo(
    () => resolveOpeningAmount(customerOpeningBase, sourceRows, fromDate),
    [customerOpeningBase, sourceRows, fromDate],
  );

  const buildRowsWithOpening = useCallback(
    (rows, sortDir = ledgerSort) =>
      withOpeningBalance(rows, openingAmount, sortDir),
    [openingAmount, ledgerSort],
  );

  const sourceKey = useMemo(() => {
    const d = CustomerLedgers?.data;
    const len = Array.isArray(filteredSourceRows)
      ? filteredSourceRows.length
      : 0;
    const stamp =
      d && !Array.isArray(d)
        ? `${len}-${filteredSourceRows[0]?.date || ""}-${
            filteredSourceRows[len - 1]?.date || ""
          }`
        : "empty";
    return `${id}:${CurrentTab}:${ledgerSort}:${fromDate}:${toDate}:ob${openingAmount}:${stamp}`;
  }, [
    id,
    CurrentTab,
    ledgerSort,
    fromDate,
    toDate,
    openingAmount,
    filteredSourceRows,
    CustomerLedgers?.data,
  ]);

  useEffect(() => {
    dispatch(fetchCustomers());
  }, [dispatch]);

  useEffect(() => {
    if (id) {
      dispatch(fetchCustomerLedger({ id, sort: ledgerSort }));
    }
  }, [id, ledgerSort, dispatch]);

  useEffect(() => {
    if (skipLiveHydrateRef.current) {
      skipLiveHydrateRef.current = false;
      return;
    }
    if (activeSavedId) return;
    if (hydrateKeyRef.current === sourceKey) return;
    hydrateKeyRef.current = sourceKey;
    setEditRows(buildRowsWithOpening(filteredSourceRows, ledgerSort));
    setDirty(false);
  }, [
    sourceKey,
    filteredSourceRows,
    ledgerSort,
    activeSavedId,
    buildRowsWithOpening,
  ]);

  /** While a saved record is open: keep edited prices, pull in new live entries. */
  useEffect(() => {
    if (!activeSavedId) return;
    setEditRows((prev) => {
      const inRangePrev = filterRowsByDateRange(prev, fromDate, toDate);
      return buildRowsWithOpening(
        mergeEditsWithLive(inRangePrev, filteredSourceRows, ledgerSort).filter(
          (r) => !isOpeningRow(r),
        ),
        ledgerSort,
      );
    });
  }, [
    sourceKey,
    activeSavedId,
    filteredSourceRows,
    ledgerSort,
    fromDate,
    toDate,
    buildRowsWithOpening,
  ]);

  /** Leaving saved tab → return to live hydrate for the new tab. */
  useEffect(() => {
    if (!activeSavedId || !activeSavedMeta) return;
    const savedTab = TAB_KEYS.includes(activeSavedMeta.tab)
      ? activeSavedMeta.tab
      : "All";
    if (CurrentTab === savedTab) return;
    setActiveSavedId(null);
    setActiveSavedMeta(null);
    hydrateKeyRef.current = "";
  }, [CurrentTab, activeSavedId, activeSavedMeta]);

  const loadSavedRecords = useCallback(async () => {
    if (!id) return;
    setRecordsLoading(true);
    try {
      const res = await GetCustomerLedgerRecordsApi(id);
      const list = res?.data?.data?.payload;
      setSavedRecords(Array.isArray(list) ? list : []);
    } catch (e) {
      setSavedRecords([]);
      ErrorToast(
        e.response?.data?.error?.msg || "Could not load saved ledger records",
      );
    } finally {
      setRecordsLoading(false);
    }
  }, [id]);

  useEffect(() => {
    loadSavedRecords();
  }, [loadSavedRecords]);

  const handlePrint = useReactToPrint({
    documentTitle: `Customer-Ledger-${customerRecord?.name || id}`,
    content: () => contentToPrint.current,
    removeAfterPrint: true,
    pageStyle: `
      @page {
        size: A4 portrait;
        margin: 6mm 7mm;
      }
      @media print {
        html, body {
          width: 210mm;
          margin: 0 !important;
          padding: 0 !important;
          background: #fff !important;
          -webkit-print-color-adjust: exact !important;
          print-color-adjust: exact !important;
          color: #000 !important;
          font-size: 8.5pt !important;
        }
        .ledger-a4-root {
          width: 100% !important;
          max-width: 100% !important;
          margin: 0 !important;
          padding: 0 !important;
        }
        .ledger-a4-banner {
          display: block !important;
          width: 100% !important;
          max-width: 100% !important;
          height: auto !important;
          max-height: none !important;
          object-fit: contain !important;
          object-position: center top !important;
          border-width: 1px !important;
          border-radius: 0 !important;
          page-break-inside: avoid !important;
          break-inside: avoid !important;
          -webkit-print-color-adjust: exact !important;
          print-color-adjust: exact !important;
        }
        .ledger-a4-bar {
          padding: 4px 7px !important;
          font-size: 9pt !important;
          margin-top: 2px !important;
          background: #0b3a3b !important;
          color: #fff !important;
          border: 0.7pt solid #000 !important;
          -webkit-print-color-adjust: exact !important;
          print-color-adjust: exact !important;
        }
        .ledger-a4-bar span {
          color: #fff !important;
        }
        .ledger-a4-bar-meta {
          font-size: 7.5pt !important;
        }
        .ledger-a4-details {
          border: 0.7pt solid #000 !important;
          -webkit-print-color-adjust: exact !important;
          print-color-adjust: exact !important;
        }
        .ledger-a4-details > div {
          padding: 3px 6px !important;
          border-color: #000 !important;
        }
        .ledger-a4-details .ledger-a4-label {
          font-size: 6.5pt !important;
          width: auto !important;
          color: #111 !important;
          font-weight: 800 !important;
        }
        .ledger-a4-details .ledger-a4-value {
          font-size: 8pt !important;
          color: #000 !important;
          font-weight: 700 !important;
        }
        .ledger-a4-legend {
          display: none !important;
        }
        .ledger-a4-table {
          width: 100% !important;
          table-layout: fixed !important;
          border-collapse: collapse !important;
          font-size: 7.5pt !important;
        }
        .ledger-a4-table thead {
          display: table-header-group !important;
        }
        .ledger-a4-table tr {
          page-break-inside: avoid !important;
        }
        .ledger-a4-table th,
        .ledger-a4-table td {
          color: #000 !important;
          border: 0.55pt solid #000 !important;
          padding: 2px 3px !important;
          vertical-align: top !important;
          word-wrap: break-word !important;
          overflow-wrap: anywhere !important;
          -webkit-print-color-adjust: exact !important;
          print-color-adjust: exact !important;
        }
        .ledger-a4-table thead,
        .ledger-a4-thead {
          background: #0b3a3b !important;
          -webkit-print-color-adjust: exact !important;
          print-color-adjust: exact !important;
        }
        .ledger-a4-table thead th,
        .ledger-a4-thead th {
          background: #0b3a3b !important;
          color: #fff !important;
          font-size: 7pt !important;
          font-weight: 800 !important;
          border-color: #000 !important;
          -webkit-print-color-adjust: exact !important;
          print-color-adjust: exact !important;
        }
        /* Row tints — strong enough for B&W printers */
        .ledger-a4-table tbody tr.bg-sky-100,
        .ledger-a4-table tbody tr.bg-sky-100 td {
          background: #c8d9e8 !important;
        }
        .ledger-a4-table tbody tr.bg-fuchsia-100,
        .ledger-a4-table tbody tr.bg-fuchsia-100 td {
          background: #d9c8d8 !important;
        }
        .ledger-a4-table tbody tr.bg-slate-100,
        .ledger-a4-table tbody tr.bg-slate-100 td,
        .ledger-a4-table tbody tr.bg-gray-100,
        .ledger-a4-table tbody tr.bg-gray-100 td {
          background: #e4e4e4 !important;
        }
        .ledger-a4-table tbody tr.bg-amber-50,
        .ledger-a4-table tbody tr.bg-amber-50 td {
          background: #e8d9b8 !important;
        }
        .ledger-a4-table tbody tr.bg-emerald-50,
        .ledger-a4-table tbody tr.bg-emerald-50 td {
          background: #c5ddd0 !important;
        }
        .ledger-a4-table tbody tr.bg-violet-50,
        .ledger-a4-table tbody tr.bg-violet-50 td {
          background: #d4cce0 !important;
        }
        .ledger-a4-col-date { width: 10% !important; }
        .ledger-a4-col-name { width: 30% !important; }
        .ledger-a4-col-qty { width: 7% !important; }
        .ledger-a4-col-price { width: 10% !important; }
        .ledger-a4-col-cr,
        .ledger-a4-col-dr,
        .ledger-a4-col-balance { width: 13% !important; }
        .ledger-a4-dt {
          font-size: 6.5pt !important;
          font-weight: 700 !important;
          line-height: 1.1 !important;
          color: #000 !important;
        }
        .ledger-a4-time {
          display: none !important;
        }
        .ledger-a4-inv {
          font-size: 6.5pt !important;
          font-weight: 600 !important;
          line-height: 1.1 !important;
          color: #000 !important;
        }
        .ledger-a4-name {
          font-size: 7.5pt !important;
          font-weight: 600 !important;
          line-height: 1.15 !important;
          color: #000 !important;
        }
        .ledger-a4-desc {
          font-size: 6.5pt !important;
          font-weight: 600 !important;
          line-height: 1.15 !important;
          color: #000 !important;
        }
        .ledger-a4-summary-section {
          overflow: visible !important;
          page-break-inside: avoid !important;
          break-inside: avoid !important;
        }
        .ledger-a4-summary-wrap {
          overflow: visible !important;
          width: 100% !important;
        }
        .ledger-a4-summary-head {
          padding: 5px 8px !important;
          font-size: 8.5pt !important;
          font-weight: 800 !important;
          background: #0b3a3b !important;
          color: #fff !important;
          border: 0.7pt solid #000 !important;
          border-bottom: none !important;
          -webkit-print-color-adjust: exact !important;
          print-color-adjust: exact !important;
        }
        .ledger-a4-summary-grid {
          min-width: 0 !important;
          width: 100% !important;
          display: grid !important;
          grid-template-columns: repeat(4, minmax(0, 1fr)) !important;
          border: 0.8pt solid #000 !important;
          background: #fff !important;
          -webkit-print-color-adjust: exact !important;
          print-color-adjust: exact !important;
        }
        .ledger-a4-summary-cell {
          padding: 6px 7px !important;
          border: 0.6pt solid #000 !important;
          background: #fff !important;
          -webkit-print-color-adjust: exact !important;
          print-color-adjust: exact !important;
        }
        /* Checkerboard-ish fills so B&W still separates cards */
        .ledger-a4-summary-cell:nth-child(odd) {
          background: #f2f2f2 !important;
        }
        .ledger-a4-summary-cell:nth-child(even) {
          background: #ffffff !important;
        }
        .ledger-a4-summary-cell.bg-sky-100 {
          background: #c5d8ea !important;
        }
        .ledger-a4-summary-cell.bg-fuchsia-100 {
          background: #dbc5d6 !important;
        }
        .ledger-a4-summary-cell.bg-danger\/5,
        .ledger-a4-summary-cell[class*="bg-danger"] {
          background: #e8e8e8 !important;
          border-width: 1.1pt !important;
          box-shadow: inset 0 0 0 0.8pt #000 !important;
        }
        .ledger-a4-summary-label,
        .ledger-a4-summary-cell .ledger-a4-summary-label {
          font-size: 7pt !important;
          line-height: 1.25 !important;
          font-weight: 800 !important;
          color: #111 !important;
          letter-spacing: 0.02em !important;
          opacity: 1 !important;
        }
        .ledger-a4-summary-value,
        .ledger-a4-summary-cell .ledger-a4-summary-value {
          font-size: 10pt !important;
          line-height: 1.25 !important;
          font-weight: 800 !important;
          color: #000 !important;
          opacity: 1 !important;
        }
        .ledger-a4-running {
          padding: 7px 9px !important;
          font-size: 10pt !important;
          font-weight: 800 !important;
          background: #cfd8dc !important;
          border: 0.8pt solid #000 !important;
          border-top: 1.6pt solid #000 !important;
          color: #000 !important;
          -webkit-print-color-adjust: exact !important;
          print-color-adjust: exact !important;
        }
        .ledger-a4-running span {
          color: #000 !important;
          font-weight: 800 !important;
        }
        input { display: none !important; }
        .print-price-value {
          display: block !important;
          color: #000 !important;
          font-weight: 700 !important;
          font-size: 7.5pt !important;
        }
        .text-ink, .text-black, .text-muted, .text-success, .text-danger,
        .text-sky-800, .text-sky-950, .text-fuchsia-800, .text-fuchsia-950,
        .text-inkSecondary, .text-main {
          color: #000 !important;
        }
        .bg-sky-100 { background-color: #c5d8ea !important; }
        .bg-fuchsia-100 { background-color: #dbc5d6 !important; }
        .bg-main\/10, .bg-main\/95, .bg-main {
          -webkit-print-color-adjust: exact !important;
          print-color-adjust: exact !important;
        }
      }
    `,
  });

  const columns = useMemo(() => visibleColumns(CurrentTab), [CurrentTab]);

  const handlePriceChange = (rowKey, value) => {
    setDirty(true);
    setEditRows((prev) => {
      const next = prev.map((row) =>
        row._rowKey === rowKey ? applyPriceToRow(row, value) : row,
      );
      return refreshBalancesPreserveOrder(next);
    });
  };

  const handleOpenRecord = async (record) => {
    if (!record) return;
    const tab = TAB_KEYS.includes(record.tab) ? record.tab : "All";
    skipLiveHydrateRef.current = true;
    setCurrentTab(tab);
    setActiveSavedId(record._id);
    setActiveSavedMeta(record);
    hydrateKeyRef.current = `saved:${record._id}:${tab}`;

    let liveAll = pickTabRows(CustomerLedgers?.data, tab);
    let live = filterRowsByDateRange(liveAll, fromDate, toDate);
    try {
      const result = await dispatch(
        fetchCustomerLedger({ id, sort: ledgerSort }),
      );
      if (result?.payload && !Array.isArray(result.payload)) {
        liveAll = pickTabRows(result.payload, tab);
        live = filterRowsByDateRange(liveAll, fromDate, toDate);
      }
    } catch {
      /* keep current live */
    }

    const obBase =
      Number(record.customerSnapshot?.opening_balance) ||
      Number(customerRecord?.opening_balance) ||
      0;
    const obAmt = resolveOpeningAmount(obBase, liveAll, fromDate);
    const merged = mergeEditsWithLive(
      filterRowsByDateRange(record.rows || [], fromDate, toDate),
      live,
      ledgerSort,
    ).filter((r) => !isOpeningRow(r));

    setEditRows(withOpeningBalance(merged, obAmt, ledgerSort));
    setDirty(true);
    window.scrollTo({ top: 0, behavior: "smooth" });
    SuccessToast("Saved ledger opened — your prices kept; newer entries added");
  };

  const handleBackToLive = () => {
    setActiveSavedId(null);
    setActiveSavedMeta(null);
    hydrateKeyRef.current = "";
    setEditRows(buildRowsWithOpening(filteredSourceRows, ledgerSort));
    setDirty(false);
  };

  const handleClearDates = () => {
    setFromDate("");
    setToDate("");
  };

  const dateRangeLabel =
    fromDate || toDate
      ? `${fromDate || "…"} → ${toDate || localYmd()}`
      : "All dates";

  const resolveBranchId = () => {
    const fromCustomer = customerRecord?.branchId;
    if (fromCustomer?._id) return String(fromCustomer._id);
    if (fromCustomer) return String(fromCustomer);
    if (sessionBranchId?._id) return String(sessionBranchId._id);
    return String(sessionBranchId || "");
  };

  const finalBalance =
    editRows.length > 0
      ? Number(
          [...editRows].sort(
            (a, b) => Number(a.date || 0) - Number(b.date || 0),
          )[editRows.length - 1]?.balance || 0,
        )
      : 0;

  /** Figure cards follow visible table (filters + price edits). */
  const tableFigures = useMemo(() => {
    const fromRows = figuresFromRows(editRows);
    // Total Amount = same field as /branch/customers "Total" column
    const rawTotal =
      activeSavedId &&
      activeSavedMeta?.customerSnapshot &&
      activeSavedMeta.customerSnapshot.total != null
        ? activeSavedMeta.customerSnapshot.total
        : customerRecord?.total;
    const accountTotal =
      rawTotal != null && rawTotal !== "" ? Number(rawTotal) : NaN;
    return {
      ...fromRows,
      total: Number.isFinite(accountTotal) ? accountTotal : fromRows.total,
      // Keep in sync with running Balance column
      recieveable: finalBalance,
    };
  }, [
    editRows,
    finalBalance,
    activeSavedId,
    activeSavedMeta,
    customerRecord?.total,
  ]);

  const handleSaveRecord = async () => {
    if (!editRows.length) {
      WarningToast("No ledger rows to save");
      return;
    }
    const branchId = resolveBranchId();
    if (!branchId) {
      WarningToast("Branch is required to save record");
      return;
    }
    setSaveLoading(true);
    try {
      const rowsPayload = editRows.map(({ _rowKey, ...rest }) => rest);
      const response = await CreateCustomerLedgerRecordApi({
        customerId: id,
        branchId,
        tab: CurrentTab,
        customerSnapshot: {
          name: customerRecord?.name || "",
          contact:
            customerRecord?.contact != null
              ? String(customerRecord.contact)
              : "",
          address: customerRecord?.address || "",
          total:
            customerRecord?.total != null
              ? Number(customerRecord.total)
              : tableFigures.total,
          opening_balance: tableFigures.opening_balance,
          discount: tableFigures.discount,
          return_amount: tableFigures.return_amount,
          recieved: tableFigures.recieved,
          recieveable: tableFigures.recieveable,
        },
        rows: rowsPayload,
        stats: {
          rowCount: rowsPayload.length,
          finalBalance,
          tab: CurrentTab,
        },
        date: Math.floor(Date.now() / 1000),
      });
      if (response.data?.success) {
        SuccessToast("Ledger record saved");
        setDirty(false);
        await loadSavedRecords();
      } else {
        ErrorToast(response.data?.error?.msg || "Failed to save record");
      }
    } catch (err) {
      ErrorToast(err?.response?.data?.error?.msg || "Failed to save record");
    } finally {
      setSaveLoading(false);
    }
  };

  const handleDeleteRecord = async (record) => {
    if (!record?._id) return;
    try {
      const response = await DeleteCustomerLedgerRecordApi(record._id);
      if (response.data?.success) {
        SuccessToast("Record deleted");
        if (activeSavedId === record._id) {
          handleBackToLive();
        }
        await loadSavedRecords();
      } else {
        ErrorToast(response.data?.error?.msg || "Failed to delete");
      }
    } catch (err) {
      ErrorToast(err?.response?.data?.error?.msg || "Failed to delete");
    }
  };

  const renderLedgerTable = (rows, opts = {}) => {
    const { editable = false, tab = CurrentTab } = opts;
    const cols = visibleColumns(tab);
    if (!rows?.length) {
      return (
        <p className="text-center text-muted py-8 border border-t-0 border-main bg-subtle/40">
          No ledger rows for this tab / date range.
        </p>
      );
    }
    return (
      <div className="w-full overflow-x-auto border border-t-0 border-main">
        <div className="ledger-a4-legend flex flex-wrap items-center gap-2 px-3 py-2 bg-subtle border-b border-line print:hidden">
          <span className="text-[11px] font-bold uppercase tracking-wide text-inkSecondary mr-1">
            Row type
          </span>
          {LEDGER_ROW_LEGEND.map((item) => (
            <span
              key={item.key}
              className={`inline-flex items-center gap-1.5 text-[11px] font-semibold px-2 py-0.5 rounded border ${item.className}`}
            >
              <span
                className={`w-2.5 h-2.5 rounded-sm border border-black/10 ${item.className.split(" ")[0]}`}
              />
              {item.label}
            </span>
          ))}
        </div>
        <table className="ledger-a4-table w-full text-sm border-collapse">
          <thead className="ledger-a4-thead bg-main text-white">
            <tr>
              {cols.map((dt) => (
                <th
                  key={dt.id}
                  className={`ledger-a4-col-${dt.id} border border-main/80 print:border-black px-1.5 py-1.5 sm:px-2.5 sm:py-2 font-semibold print:text-black ${ledgerColAlign(
                    dt.id,
                  )} ${ledgerColWidth(dt.id)}`}
                >
                  {dt.id === "price"
                    ? "Price"
                    : dt.id === "date"
                      ? "Date / Inv"
                      : dt.title}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((item, index) => {
              const canEditPrice =
                editable &&
                cols.some((c) => c.id === "price") &&
                Number(item.qty || 0) > 0;
              const kind = ledgerRowKind(item);
              const isOpen = isOpeningRow(item);
              return (
                <tr
                  key={item._rowKey || `${item.invoice}-${index}`}
                  className={`${LEDGER_ROW_COLORS[kind] || LEDGER_ROW_COLORS.other} ${
                    isOpen ? "font-semibold border-b-2 border-slate-300" : ""
                  }`}
                >
                  {cols.map((dt) => (
                    <td
                      key={dt.id}
                      className={`ledger-a4-col-${dt.id} border border-main/40 px-1.5 py-1 sm:px-2.5 sm:py-1.5 align-middle ${ledgerColAlign(
                        dt.id,
                      )} ${
                        ["cr", "dr", "balance"].includes(dt.id)
                          ? "font-semibold"
                          : ""
                      }`}
                    >
                      {dt.id === "price" && canEditPrice ? (
                        <>
                          <input
                            type="number"
                            className="w-full max-w-[6rem] ml-auto block px-2 py-1 border border-line rounded text-sm font-semibold text-ink outline-none focus:border-main bg-white text-right tabular-nums print:hidden"
                            value={item.price ?? ""}
                            onChange={(e) =>
                              handlePriceChange(item._rowKey, e.target.value)
                            }
                          />
                          <span className="hidden print:block print-price-value font-bold text-black text-right tabular-nums">
                            {Number(item.price || 0).toLocaleString("en-PK")}
                          </span>
                        </>
                      ) : dt.id === "date" || dt.id === "invoice" ? (
                        <div className="flex flex-col gap-0 min-w-0 text-left leading-tight">
                          {isOpeningRow(item) || !item.date ? (
                            <span className="text-[10px] text-muted">—</span>
                          ) : (
                            <>
                              <span
                                className="ledger-a4-dt text-[10px] text-ink whitespace-nowrap"
                                style={{ fontWeight: 700 }}
                              >
                                {formatDate(item.date)}
                              </span>
                              <span
                                className="ledger-a4-time text-[10px] text-ink whitespace-nowrap print:hidden"
                                style={{ fontWeight: 700 }}
                              >
                                {(() => {
                                  const m = parseAppDate(item.date);
                                  return m ? m.format("hh:mm A") : "—";
                                })()}
                              </span>
                            </>
                          )}
                          <span
                            className="ledger-a4-inv text-[10px] break-all"
                            style={{ fontWeight: 600 }}
                          >
                            {item.invoice != null &&
                            String(item.invoice).trim() !== ""
                              ? item.invoice
                              : "—"}
                          </span>
                        </div>
                      ) : dt.id === "name" || dt.id === "desc" ? (
                        <div className="flex flex-col gap-0.5 min-w-0 text-left">
                          <span className="ledger-a4-name font-semibold leading-snug break-words">
                            {item.name != null &&
                            String(item.name).trim() !== ""
                              ? item.name
                              : "—"}
                          </span>
                          {item.desc != null &&
                            String(item.desc).trim() !== "" &&
                            String(item.desc) !== String(item.name ?? "") && (
                              <span
                                className="ledger-a4-desc text-[11px] leading-snug break-words"
                                style={{ fontWeight: 700 }}
                              >
                                {item.desc}
                              </span>
                            )}
                        </div>
                      ) : dt.id === "qty" ? (
                        <div className="flex flex-col gap-0.5 min-w-0 text-left">
                          <span className="font-bold leading-snug break-words print:text-black">
                            {Number(item[dt.id] || 0).toLocaleString("en-PK")}
                          </span>
                        </div>
                      ) : dt.id === "price" ? (
                        <div className="flex flex-col gap-0.5 min-w-0 text-right">
                          <span className="font-bold leading-snug break-words print:text-black text-ink">
                            {Number(item[dt.id] || 0).toLocaleString("en-PK")}
                          </span>
                        </div>
                      ) : [
                          "payable",
                          "cr",
                          "dr",
                          "price",
                          "discount",
                          "paid",
                          "total",
                          "balance",
                        ].includes(dt.id) ? (
                        Number(item[dt.id] || 0).toLocaleString("en-PK")
                      ) : dt.id === "qty" ? (
                        Number(item[dt.id] || 0).toLocaleString("en-PK")
                      ) : (
                        (item[dt.id] ?? "—")
                      )}
                    </td>
                  ))}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    );
  };

  const displayCustomer =
    (activeSavedId && activeSavedMeta?.customerSnapshot) || customerRecord;

  const statsCards = () => {
    const c = tableFigures;
    const items = [
      {
        key: "opening",
        label: "Opening Balance",
        value: formatRs(c.opening_balance),
        tone: "text-ink",
      },
      {
        key: "qty",
        label: "Total Qty (not SH)",
        value: Number(c.total_qty_not_sh || 0).toLocaleString("en-PK"),
        tone: "text-sky-800",
        bg: "bg-sky-100",
      },
      {
        key: "shopping",
        label: "Total Shopping (SH)",
        value: formatRs(c.total_shopping_sh),
        tone: "text-fuchsia-800",
        bg: "bg-fuchsia-100",
      },
      {
        key: "amount",
        label: "Total Amount",
        value: formatRs(c.total),
        tone: "text-ink",
      },
      {
        key: "return",
        label: "Total Return",
        value: formatRs(c.return_amount),
        tone: "text-success",
      },
      {
        key: "discount",
        label: "Total Discount",
        value: formatRs(c.discount),
        tone: "text-success",
      },
      {
        key: "paid",
        label: "Paid",
        value: formatRs(c.recieved),
        tone: "text-success",
      },
      {
        key: "remaining",
        label: "Remaining",
        value: formatRs(c.recieveable),
        tone: "text-danger",
        emphasize: true,
      },
    ];
    return (
      <div className="ledger-a4-summary-section mt-0 border border-t-0 border-main overflow-hidden">
        <div className="ledger-a4-summary-head bg-main/95 text-white px-3 py-1.5 text-xs font-bold uppercase tracking-wide">
          Summary — {fromDate || toDate ? dateRangeLabel : "All dates"}
          {editRows.length ? ` · ${editRows.length} rows` : ""}
        </div>
        <div className="ledger-a4-summary-wrap w-full overflow-x-auto">
          <div className="ledger-a4-summary-grid grid grid-cols-2 sm:grid-cols-4 divide-x divide-y divide-line bg-card">
            {items.map((item) => (
              <div
                key={item.key}
                className={`ledger-a4-summary-cell px-2 sm:px-3 py-3 flex flex-col gap-0.5 min-w-0 ${
                  item.bg || ""
                } ${item.emphasize ? "bg-danger/5" : ""}`}
              >
                <span className="ledger-a4-summary-label text-[10px] sm:text-[11px] font-semibold uppercase tracking-wide text-muted leading-snug">
                  {item.label}
                </span>
                <span
                  className={`ledger-a4-summary-value text-sm sm:text-base font-bold tabular-nums leading-tight break-words ${item.tone}`}
                >
                  {item.value}
                </span>
              </div>
            ))}
          </div>
        </div>
        {editRows.length > 0 && (
          <div className="ledger-a4-running flex flex-wrap items-center justify-between gap-2 px-4 py-3 bg-main/10 border-t-2 border-main">
            <span className="text-sm sm:text-base font-bold text-ink">
              Running balance after last visible row
            </span>
            <span className="text-base sm:text-lg font-extrabold tabular-nums text-main">
              {formatRs(finalBalance)}
            </span>
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="flex flex-col items-center justify-center pb-12 px-2">
      <div className="mt-[12vh] w-full max-w-5xl flex items-center justify-center gap-3 relative mb-4 flex-wrap">
        <button
          type="button"
          className="border-2 border-ink p-2 rounded-full hover:text-white hover:bg-ink transition-colors ease-in-out duration-200 cursor-pointer absolute left-0 top-1/2 -translate-y-1/2"
          onClick={() => navigate(-1)}
          aria-label="Go back"
        >
          <FaArrowLeft />
        </button>
        <button
          type="button"
          className="px-4 py-2 border-2 border-ink hover:rounded-lg transition-colors ease-in-out duration-200 hover:bg-ink/80 bg-ink text-white cursor-pointer min-w-[140px] flex justify-center items-center font-bold"
          onClick={() => handlePrint()}
        >
          PRINT
        </button>
        <button
          type="button"
          disabled={saveLoading || !editRows.length}
          className="px-4 py-2 border-2 border-main bg-main text-white hover:bg-mainHover rounded-lg transition-colors cursor-pointer min-w-[160px] flex justify-center items-center gap-2 font-bold disabled:opacity-50"
          onClick={handleSaveRecord}
        >
          <FaSave />
          {saveLoading ? "Saving…" : dirty ? "Save Record *" : "Save Record"}
        </button>
      </div>

      <p className="text-xs text-muted mb-3 px-2 text-center max-w-5xl leading-relaxed">
        Edit <strong>Price</strong> to recalculate CR/DR and running Balance
        (display only — live invoices unchanged). <strong>Save Record</strong>{" "}
        stores a snapshot. <strong>Open</strong> loads that snapshot here so you
        can keep updating prices from where you left off.
      </p>

      {activeSavedId && (
        <div className="w-full max-w-5xl mb-3 px-2">
          <div className="flex flex-wrap items-center justify-between gap-2 rounded-xl border border-main/30 bg-brandSoft px-4 py-2.5">
            <p className="text-sm text-ink">
              Viewing saved ledger
              {activeSavedMeta
                ? ` · ${formatRecordWhen(activeSavedMeta)} · Tab ${
                    activeSavedMeta.tab === "SH"
                      ? "Customer"
                      : activeSavedMeta.tab || "All"
                  }`
                : ""}
              . Your saved prices stay; new ledger entries after that are merged
              in. Edit prices further, then Save again.
            </p>
            <button
              type="button"
              onClick={handleBackToLive}
              className="text-xs font-bold text-main hover:underline shrink-0"
            >
              Back to live ledger
            </button>
          </div>
        </div>
      )}

      <div className="w-full max-w-5xl mt-2 px-1 space-y-3">
        <div className="flex flex-wrap items-stretch gap-3">
          <div className="flex-1 min-w-[260px]">
            <ReportDateRangeBar
              compact
              fromDate={fromDate}
              toDate={toDate}
              onFromChange={setFromDate}
              onToChange={setToDate}
              periodLabel={dateRangeLabel}
              refreshing={CustomerLedgers.loading}
            />
          </div>
          {(fromDate || toDate) && (
            <button
              type="button"
              onClick={handleClearDates}
              className="self-end h-10 px-3 rounded-lg border border-line bg-subtle text-xs font-bold text-inkSecondary hover:border-main/40 hover:text-main"
            >
              All dates
            </button>
          )}
        </div>
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div className="flex flex-1 min-w-[240px] rounded-xl overflow-hidden bg-subtle border-2 border-ink">
            {TAB_KEYS.map((tb) => (
              <div
                key={tb}
                className={`text-center py-3 font-poppins transition-all ease-in-out duration-300 cursor-pointer w-full ${
                  CurrentTab === tb ? "bg-ink text-white" : "text-inkSecondary"
                }`}
                onClick={() => setCurrentTab(tb)}
              >
                {tb === "SH" ? "Customer" : tb}
              </div>
            ))}
          </div>
          <div
            className="inline-flex items-center rounded-lg border border-line bg-card p-0.5 shadow-soft"
            role="group"
            aria-label="Sort ledger by date"
          >
            <button
              type="button"
              onClick={() => setLedgerSort("desc")}
              className={`px-3 h-9 rounded-md text-xs font-bold transition-colors ${
                ledgerSort === "desc"
                  ? "bg-main text-white"
                  : "text-inkSecondary hover:bg-subtle"
              }`}
            >
              Newest first
            </button>
            <button
              type="button"
              onClick={() => setLedgerSort("asc")}
              className={`px-3 h-9 rounded-md text-xs font-bold transition-colors ${
                ledgerSort === "asc"
                  ? "bg-main text-white"
                  : "text-inkSecondary hover:bg-subtle"
              }`}
            >
              Oldest first
            </button>
          </div>
        </div>
      </div>

      <div
        ref={contentToPrint}
        className="ledger-a4-root flex flex-col w-full max-w-5xl px-2 mt-4"
      >
        <img
          src={`/banner.jpeg`}
          alt="banner"
          className="ledger-a4-banner w-full h-auto border-2 border-[#002650] rounded-t-lg"
        />

        <div className="ledger-a4-bar flex w-full bg-main text-white items-center justify-between gap-3 py-2 px-3 font-bold text-base sm:text-lg font-quicksand mt-1">
          <span>Customer Details</span>
          <span className="ledger-a4-bar-meta text-xs sm:text-sm font-semibold opacity-90 font-poppins truncate">
            {displayCustomer?.name || "—"}
          </span>
        </div>
        <div className="ledger-a4-details grid grid-cols-1 sm:grid-cols-3 border border-main border-t-0 bg-card divide-y sm:divide-y-0 sm:divide-x divide-main/40">
          {[
            { label: "Name", value: displayCustomer?.name || "—" },
            { label: "Contact", value: displayCustomer?.contact ?? "—" },
            { label: "Address", value: displayCustomer?.address || "—" },
          ].map((field) => (
            <div
              key={field.label}
              className="px-3 py-2.5 flex flex-col sm:flex-row sm:items-baseline gap-0.5 sm:gap-3 min-w-0"
            >
              <span className="ledger-a4-label text-[11px] font-bold uppercase tracking-wide text-muted shrink-0 w-[4.5rem]">
                {field.label}
              </span>
              <span className="ledger-a4-value text-sm sm:text-base font-semibold text-ink break-words">
                {field.value}
              </span>
            </div>
          ))}
        </div>

        <div className="ledger-a4-bar flex w-full bg-main text-white items-center justify-between gap-3 flex-wrap py-2 px-3 font-bold text-base sm:text-lg font-quicksand mt-1">
          <span>Customer Ledger Report</span>
          <span className="ledger-a4-bar-meta text-xs sm:text-sm font-semibold opacity-90 font-poppins">
            {fromDate || toDate ? dateRangeLabel : "All dates"}
            {" · "}
            {CurrentTab === "SH" ? "Customer" : CurrentTab}
            {" · "}
            {ledgerSort === "desc" ? "Newest first" : "Oldest first"}
          </span>
        </div>

        {CustomerLedgers.loading ? (
          <p className="text-center py-10 text-muted border border-t-0 border-main">
            Loading ledger…
          </p>
        ) : (
          <>
            {renderLedgerTable(editRows, { editable: true, tab: CurrentTab })}
            {statsCards()}
          </>
        )}
      </div>

      <div className="w-full max-w-5xl mt-8 px-2">
        <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
          <div>
            <h2 className="text-lg font-bold text-ink">Saved Ledger Records</h2>
            <p className="text-xs text-muted">
              Open loads that saved entry into the table above (editable). Live
              data returns via &quot;Back to live ledger&quot;.
            </p>
          </div>
          <button
            type="button"
            onClick={loadSavedRecords}
            className="text-xs font-bold text-main hover:underline"
          >
            Refresh
          </button>
        </div>

        {recordsLoading ? (
          <p className="text-sm text-muted py-6 text-center">
            Loading records…
          </p>
        ) : savedRecords.length === 0 ? (
          <div className="rounded-xl border border-dashed border-line bg-subtle px-4 py-8 text-center text-sm text-muted">
            No saved records yet. Edit prices, then click{" "}
            <strong>Save Record</strong>.
          </div>
        ) : (
          <div className="w-full overflow-x-auto rounded-xl border border-line bg-card shadow-soft">
            <table className="w-full text-sm border-collapse">
              <thead className="bg-main text-white">
                <tr>
                  <th className="border border-main px-3 py-2 text-left">
                    Saved at
                  </th>
                  <th className="border border-main px-3 py-2 text-left">
                    Tab
                  </th>
                  <th className="border border-main px-3 py-2 text-right">
                    Rows
                  </th>
                  <th className="border border-main px-3 py-2 text-right">
                    Final balance
                  </th>
                  <th className="border border-main px-3 py-2 text-center">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody>
                {savedRecords.map((record) => (
                  <tr
                    key={record._id}
                    className="font-quicksand hover:bg-subtle/70 transition-colors"
                  >
                    <td className="border border-line px-3 py-2 whitespace-nowrap text-inkSecondary">
                      {formatRecordWhen(record)}
                    </td>
                    <td className="border border-line px-3 py-2">
                      {record.tab === "SH" ? "Customer" : record.tab || "All"}
                    </td>
                    <td className="border border-line px-3 py-2 text-right tabular-nums">
                      {record.stats?.rowCount ??
                        (Array.isArray(record.rows) ? record.rows.length : 0)}
                    </td>
                    <td className="border border-line px-3 py-2 text-right tabular-nums font-semibold">
                      {formatRs(record.stats?.finalBalance)}
                    </td>
                    <td className="border border-line px-3 py-2">
                      <div className="flex justify-center gap-2">
                        <button
                          type="button"
                          className={`inline-flex items-center gap-1 px-2 py-1 text-xs font-bold rounded-lg border ${
                            activeSavedId === record._id
                              ? "border-main bg-main text-white"
                              : "border-main text-main hover:bg-main hover:text-white"
                          }`}
                          onClick={() => handleOpenRecord(record)}
                        >
                          <MdOpenInNew /> Open
                        </button>
                        <button
                          type="button"
                          className="inline-flex items-center gap-1 px-2 py-1 text-xs font-bold rounded-lg border border-danger text-danger hover:bg-danger hover:text-white"
                          onClick={() => handleDeleteRecord(record)}
                        >
                          <MdDelete /> Delete
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default CustomersLegderReport;
